import type { ReactNode } from 'react';
import { LangContext } from '@spa/i18n/LangContext';
import type { LangFn, TranslationKey } from '@spa/types/lang.types';
import { getAppSettings } from '@spa/constants/appSettings';

import enAU from '@spa/locales/en-AU.json';
import daDK from '@spa/locales/da-DK.json';
import enDK from '@spa/locales/en-DK.json';
import fiFI from '@spa/locales/fi-FI.json';
import svFI from '@spa/locales/sv-FI.json';
import enNO from '@spa/locales/en-NO.json';
import noNO from '@spa/locales/no-NO.json';
import esES from '@spa/locales/es-ES.json';
import enGB from '@spa/locales/en-GB.json';

type Messages = Record<TranslationKey, string>;

const locales: Record<string, Partial<Messages>> = {
  'en-AU': enAU,
  'da-DK': daDK,
  'en-DK': enDK,
  'fi-FI': fiFI,
  'sv-FI': svFI,
  'en-NO': enNO,
  'no-NO': noNO,
  'es-ES': esES,
  'en-GB': enGB,
};

function getLanguage(): string {
  const { language = 'en-GB' } = getAppSettings();
  const [lang, region] = language.split('-');
  return region
    ? `${lang.toLowerCase()}-${region.toUpperCase()}`
    : lang.toLowerCase();
}

function interpolate(
  template: string,
  vars?: Record<string, string | number>
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const lang = getLanguage();
  const messages = (locales[lang] ?? {}) as Messages;
  const t: LangFn = (key, vars) => interpolate(messages[key] ?? key, vars);

  return <LangContext.Provider value={t}>{children}</LangContext.Provider>;
}
